<template>
  <div>
    <v-navigation-drawer 
      app 
      class="dont-print-header modern-drawer" 
      v-model="drawer"
      width="280"
    >
      <!-- Header Section -->
      <div class="drawer-header">
        <div class="logo-section">
          <img 
            src="@/assets/content-mod-logo.svg" 
            alt="Content Moderation Logo"
            width="60"
            height="60"
          />
          <div class="logo-text">
            <h3>Vijan.AI</h3>
            <p>AI Agents Marketplace</p>
          </div>
        </div>
      </div>

      <!-- User Profile Section -->
      <div class="user-profile-section">
        <v-avatar size="48" class="user-avatar">
          <v-icon size="24">mdi-account-circle</v-icon>
        </v-avatar>
        <div class="user-details">
          <h4 class="user-name">
            {{
              user.first_name != null
                ? user.first_name + " " + user.last_name
                : user.email
            }}
          </h4>
          <p class="user-role">{{
            roleName == "SUPER ADMIN" && company.id != null
              ? "ADMIN"
              : roleName
          }}</p>
        </div>
      </div>
      <v-divider class="my-3"></v-divider>
      <div class="navigation-wrapper">
        <v-list nav dense class="navigation-list">
          <!-- Dashboard -->
          <v-list-item to="/">
            <v-list-item-icon>
              <v-icon>mdi-view-dashboard</v-icon>
            </v-list-item-icon>
            <v-list-item-title>Dashboard</v-list-item-title>
          </v-list-item>

        <!-- Super Admin Sections -->
        <template v-if="roleName == 'SUPER ADMIN' && company.id == null">
          <!-- AI Marketplace Management -->
          <v-list-group no-action :value="false">
            <template v-slot:activator>
              <v-list-item-icon>
                <v-icon>mdi-robot</v-icon>
              </v-list-item-icon>
              <v-list-item-title>AI Marketplace</v-list-item-title>
            </template>
            <v-list-item to="/ai-agents">
              <v-list-item-icon>
                <v-icon>mdi-robot-excited</v-icon>
              </v-list-item-icon>
              <v-list-item-title>AI Agents</v-list-item-title>
            </v-list-item>
            <v-list-item to="/industries">
              <v-list-item-icon>
                <v-icon>mdi-domain</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Industries</v-list-item-title>
            </v-list-item>
          </v-list-group>

          <!-- Subscription Management -->
          <v-list-group no-action :value="false">
            <template v-slot:activator>
              <v-list-item-icon>
                <v-icon>mdi-package-variant</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Subscriptions</v-list-item-title>
            </template>
            <v-list-item to="/subscriptions">
              <v-list-item-icon>
                <v-icon>mdi-package</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Plans</v-list-item-title>
            </v-list-item>
            <v-list-item to="/active-subscriptions">
              <v-list-item-icon>
                <v-icon>mdi-shield-check</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Active Subscriptions</v-list-item-title>
            </v-list-item>
            <v-list-item to="/past-subscriptions">
              <v-list-item-icon>
                <v-icon>mdi-history</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Past Subscriptions</v-list-item-title>
            </v-list-item>
            <v-list-item to="/coupons">
              <v-list-item-icon>
                <v-icon>mdi-tag-multiple</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Coupons</v-list-item-title>
            </v-list-item>
          </v-list-group>

          <!-- Companies -->
          <v-list-item to="/companies">
            <v-list-item-icon>
              <v-icon>mdi-office-building</v-icon>
            </v-list-item-icon>
            <v-list-item-title>Companies</v-list-item-title>
          </v-list-item>
        </template>

        <!-- Admin Sections -->
        <template v-if="roleName == 'ADMIN' || (roleName == 'SUPER ADMIN' && company && company.id != null)">
          <!-- AI Agents Dropdown -->
          <v-list-group no-action :value="true" v-if="availableAgents && availableAgents.length > 0">
            <template v-slot:activator>
              <v-list-item-icon>
                <v-icon>mdi-robot</v-icon>
              </v-list-item-icon>
              <v-list-item-title>AI Agents</v-list-item-title>
            </template>
            
            <!-- Dynamic AI Agents from Active Subscriptions -->
            <v-list-group 
              v-for="agent in availableAgents" 
              :key="agent.id"
              sub-group 
              no-action 
              :value="false"
            >
              <template v-slot:activator>
                <v-list-item-content>
                  <v-list-item-title>
                    <v-icon small class="mr-2">{{ getAgentIcon(agent.name) }}</v-icon>
                    {{ agent.name }}
                  </v-list-item-title>
                </v-list-item-content>
              </template>
              <v-list-item :to="`/agents/${getAgentSlug(agent.name)}/dashboard`">
                <v-list-item-icon>
                  <v-icon>mdi-view-dashboard</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Dashboard</v-list-item-title>
              </v-list-item>
              <v-list-item 
                v-if="agent.name === 'ContentMod AI'"
                :to="`/agents/${getAgentSlug(agent.name)}/moderation`"
              >
                <v-list-item-icon>
                  <v-icon>mdi-gavel</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Moderation Queue</v-list-item-title>
              </v-list-item>
              <v-list-item 
                v-if="agent.name === 'ContentMod AI'"
                :to="`/agents/${getAgentSlug(agent.name)}/posts`"
              >
                <v-list-item-icon>
                  <v-icon>mdi-post</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Posts</v-list-item-title>
              </v-list-item>
              <!-- LeadRanker AI specific navigation -->
              <v-list-item 
                v-if="agent.name === 'LeadRanker AI'"
                :to="`/agents/${getAgentSlug(agent.name)}/leads`"
              >
                <v-list-item-icon>
                  <v-icon>mdi-account-multiple</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Leads Management</v-list-item-title>
              </v-list-item>
              <v-list-item 
                v-if="agent.name === 'LeadRanker AI'"
                :to="`/agents/${getAgentSlug(agent.name)}/scoring`"
              >
                <v-list-item-icon>
                  <v-icon>mdi-target</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Scoring Results</v-list-item-title>
              </v-list-item>
              <!-- PitchCrafter AI specific navigation -->
              <v-list-item 
                v-if="agent.name === 'PitchCrafter AI'"
                :to="`/agents/${getAgentSlug(agent.name)}/proposals`"
              >
                <v-list-item-icon>
                  <v-icon>mdi-file-document-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Proposals</v-list-item-title>
              </v-list-item>
              <v-list-item 
                v-if="agent.name === 'PitchCrafter AI'"
                :to="`/agents/${getAgentSlug(agent.name)}/results`"
              >
                <v-list-item-icon>
                  <v-icon>mdi-sparkles</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Generated Pitches</v-list-item-title>
              </v-list-item>
              <!-- FollowUpBot AI specific navigation -->
              <v-list-item 
                v-if="agent.name === 'FollowUpBot AI'"
                :to="`/agents/${getAgentSlug(agent.name)}/campaigns`"
              >
                <v-list-item-icon>
                  <v-icon>mdi-bullhorn</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Campaigns</v-list-item-title>
              </v-list-item>
              <v-list-item 
                v-if="agent.name === 'FollowUpBot AI'"
                :to="`/agents/${getAgentSlug(agent.name)}/messages`"
              >
                <v-list-item-icon>
                  <v-icon>mdi-email-multiple</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Messages</v-list-item-title>
              </v-list-item>
              <v-list-item 
                v-if="agent.name === 'FollowUpBot AI'"
                :to="`/agents/${getAgentSlug(agent.name)}/analytics`"
              >
                <v-list-item-icon>
                  <v-icon>mdi-chart-bar</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Analytics</v-list-item-title>
              </v-list-item>
              <!-- CRMGenie AI specific navigation -->
              <v-list-item 
                v-if="agent.name === 'CRMGenie'"
                :to="`/agents/${getAgentSlug(agent.name)}/data-processing`"
              >
                <v-list-item-icon>
                  <v-icon>mdi-cog</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Data Processing</v-list-item-title>
              </v-list-item>
              <v-list-item 
                v-if="agent.name === 'CRMGenie'"
                :to="`/agents/${getAgentSlug(agent.name)}/enrichment`"
              >
                <v-list-item-icon>
                  <v-icon>mdi-database-plus</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Data Enrichment</v-list-item-title>
              </v-list-item>
              <v-list-item 
                v-if="agent.name === 'CRMGenie'"
                :to="`/agents/${getAgentSlug(agent.name)}/duplicates`"
              >
                <v-list-item-icon>
                  <v-icon>mdi-account-multiple-minus</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Duplicate Detection</v-list-item-title>
              </v-list-item>
              <v-list-item 
                v-if="agent.name === 'CRMGenie'"
                :to="`/agents/${getAgentSlug(agent.name)}/reports`"
              >
                <v-list-item-icon>
                  <v-icon>mdi-chart-bar</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Hygiene Reports</v-list-item-title>
              </v-list-item>
            </v-list-group>
          </v-list-group>

          <!-- Subscriptions Management -->
          <v-list-group no-action :value="false">
            <template v-slot:activator>
              <v-list-item-icon>
                <v-icon>mdi-package-variant</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Subscriptions</v-list-item-title>
            </template>
            <v-list-item to="/my-subscriptions">
              <v-list-item-icon>
                <v-icon>mdi-package-variant-closed</v-icon>
              </v-list-item-icon>
              <v-list-item-title>My Subscriptions</v-list-item-title>
            </v-list-item>
            <v-list-item to="/available-subscriptions">
              <v-list-item-icon>
                <v-icon>mdi-store</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Browse Plans</v-list-item-title>
            </v-list-item>
          </v-list-group>

          <!-- Users Management -->
          <v-list-group no-action :value="false">
            <template v-slot:activator>
              <v-list-item-icon>
                <v-icon>mdi-account</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Users</v-list-item-title>
            </template>
            <v-list-item :to="`/users`">
              <v-list-item-icon>
                <v-icon>mdi-table-of-contents</v-icon>
              </v-list-item-icon>
              <v-list-item-title>List</v-list-item-title>
            </v-list-item>
          </v-list-group>

          <!-- Settings Management -->
          <v-list-group no-action :value="false">
            <template v-slot:activator>
              <v-list-item-icon>
                <v-icon>mdi-cogs</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Settings</v-list-item-title>
            </template>
            <v-list-item :to="`/modules`">
              <v-list-item-icon>
                <v-icon>mdi-puzzle</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Modules</v-list-item-title>
            </v-list-item>
            <v-list-item :to="`/permissions`">
              <v-list-item-icon>
                <v-icon>mdi-shield-account</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Permissions</v-list-item-title>
            </v-list-item>
            <v-list-item :to="`/values`">
              <v-list-item-icon>
                <v-icon>mdi-format-list-text</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Values</v-list-item-title>
            </v-list-item>
            <v-list-item :to="`/value-lists`">
              <v-list-item-icon>
                <v-icon>mdi-format-list-bulleted</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Value Lists</v-list-item-title>
            </v-list-item>
            <v-list-item :to="`/positions`">
              <v-list-item-icon>
                <v-icon>mdi-account-tie</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Positions</v-list-item-title>
            </v-list-item>
            <v-list-item :to="`/profile/${encryptIt(user.id)}`">
              <v-list-item-icon>
                <v-icon>mdi-account-circle</v-icon>
              </v-list-item-icon>
              <v-list-item-title>My Profile</v-list-item-title>
            </v-list-item>
          </v-list-group>

          <!-- Trash Section -->
          <v-list-group no-action :value="false">
            <template v-slot:activator>
              <v-list-item-icon>
                <v-icon>mdi-trash-can-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Trash</v-list-item-title>
            </template>
            <v-list-item :to="`/users?open_trash=true`">
              <v-list-item-icon>
                <v-icon>mdi-account</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Users</v-list-item-title>
            </v-list-item>
            <v-list-group
              no-action
              sub-group
              :value="false"
            >
              <template v-slot:activator>
                <v-list-item-content>
                  <v-list-item-title>Settings</v-list-item-title>
                </v-list-item-content>
              </template>
              <v-list-item :to="`/modules?open_trash=true`" link>
                <v-list-item-icon>
                  <v-icon>mdi-arrow-expand-all</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Modules</v-list-item-title>
              </v-list-item>
              <v-list-item :to="`/permissions?open_trash=true`" link>
                <v-list-item-icon>
                  <v-icon>mdi-arrow-expand-all</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Permissions</v-list-item-title>
              </v-list-item>
              <v-list-item :to="`/positions?open_trash=true`" link>
                <v-list-item-icon>
                  <v-icon>mdi-arrow-expand-all</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Position</v-list-item-title>
              </v-list-item>
            </v-list-group>
          </v-list-group>
        </template>
        </v-list>
        
        <!-- Logout Button at Bottom -->
        <div class="logout-section">
          <v-divider class="my-3"></v-divider>
          <v-list dense>
            <v-list-item @click="logout" class="logout-item">
              <v-list-item-icon>
                <v-icon color="error">mdi-logout</v-icon>
              </v-list-item-icon>
              <v-list-item-title class="logout-text">Logout</v-list-item-title>
            </v-list-item>
          </v-list>
        </div>
      </div>
    </v-navigation-drawer>
    <v-app-bar
      app
      class="dont-print-header modern-app-bar"
      flat
      height="64"
    >
      <div class="nav-container">
        <!-- Left Section -->
        <div class="nav-left">
          <v-app-bar-nav-icon
            @click.stop="drawer = !drawer"
            class="nav-icon"
          ></v-app-bar-nav-icon>
          
          <router-link to="/" class="logo-link">
            <div class="app-logo">
              <img 
                src="@/assets/content-mod-icon.svg" 
                alt="Content Moderation" 
                width="32"
                height="32"
              />
              <span class="app-title">Vijan.AI</span>
            </div>
          </router-link>
        </div>

        <!-- Right Section -->
        <div class="nav-right">
          <!-- Super Admin Role Switch -->
          <div v-if="roleName == 'SUPER ADMIN'" class="super-admin-switch">
            <v-switch
              inset
              @change="switch_to(company.id)"
              :loading="isSwitching"
              v-model="is_acting_admin"
              :label="`${is_acting_admin ? 'ADMIN' : 'SUPER ADMIN'}`"
              color="white"
              class="white--text"
              hide-details
              dense
            ></v-switch>
          </div>
          
          <!-- User Information -->
          <div class="user-info">
            <div class="user-details">
              <div class="user-name">
                {{ user.user_name != null ? user.user_name : user.email }}
              </div>
              <div class="separator" v-if="company.name">•</div>
              <v-chip
                v-if="company.name"
                x-small
                outlined
                class="company-chip"
              >
                {{ company.name }}
              </v-chip>
            </div>
          </div>

          <!-- Profile Menu -->
          <v-menu offset-y class="profile-menu">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                class="profile-button"
                v-bind="attrs"
                v-on="on"
              >
                <v-badge
                  v-if="hide_bell == false"
                  overlap
                  color="pink"
                  icon="mdi-bell"
                  offset-x="8"
                  offset-y="8"
                >
                  <v-avatar size="40" color="primary">
                    <v-icon dark>mdi-account-circle</v-icon>
                  </v-avatar>
                </v-badge>
                <v-avatar v-else size="40" color="primary">
                  <v-icon dark>mdi-account-circle</v-icon>
                </v-avatar>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item to="/notifications" @click="hide_bell = true">
                <v-list-item-icon>
                  <v-icon>mdi-bell</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Notifications</v-list-item-title>
              </v-list-item>
              <v-list-item
                :to="`/profile/${encryptIt(user.id)}`"
                @click="hide_bell = true"
              >
                <v-list-item-icon>
                  <v-icon>mdi-account</v-icon>
                </v-list-item-icon>
                <v-list-item-title>My Profile</v-list-item-title>
              </v-list-item>
              <v-divider></v-divider>
              <v-list-item @click="logout">
                <v-list-item-icon>
                  <v-icon>mdi-logout</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Logout</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>
    </v-app-bar>

    <v-main>
      <router-view />
    </v-main>
    
    <!-- Switch to Admin Dialog -->
    <v-dialog v-model="show_company_dialog" max-width="900px" persistent>
      <v-card class="switch-admin-dialog" elevation="24">
        <!-- Header with gradient background -->
        <v-card-title class="dialog-header">
          <div class="dialog-header-content">
            <div class="dialog-logo-section">
              <div class="dialog-logo-wrapper">
                <img 
                  src="@/assets/content-mod-icon.svg" 
                  alt="Content Moderation"
                  width="40"
                  height="40"
                />
              </div>
              <div class="dialog-title-section">
                <h2 class="dialog-title">Switch to Admin</h2>
                <p class="dialog-subtitle">Select a company to manage as admin</p>
              </div>
            </div>
            <v-btn 
              icon 
              @click="show_company_dialog = false"
              class="close-btn"
            >
              <v-icon color="white">mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text class="dialog-content">
          <!-- Search Section -->
          <div class="search-section mb-6">
            <v-text-field
              v-model="form.search_keyword"
              label="Search companies..."
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              clearable
              hide-details
              class="modern-search"
              @click:clear="(form.search_keyword = null), getCompanies()"
              @keydown.enter="getCompanies"
              @change="page = 1"
            />
          </div>

          <!-- Loading State -->
          <div v-if="isLoading" class="loading-section">
            <v-progress-circular
              indeterminate
              color="primary"
              size="40"
            ></v-progress-circular>
            <p class="loading-text">Loading companies...</p>
          </div>

          <!-- Companies Table -->
          <div v-else class="companies-table-section">
            <v-data-table
              :headers="companyHeaders"
              :items="companies"
              :hide-default-footer="true"
              class="modern-companies-table"
              no-data-text="No companies found"
            >
              <template v-slot:[`item.sr_no`]="{ index }">
                <span class="company-serial">{{ index + 1 }}</span>
              </template>

              <template v-slot:[`item.is_active`]="{ item }">
                <v-chip
                  :color="item.is_active == 1 ? 'green' : 'red'"
                  dark
                  small
                  class="status-chip"
                >
                  {{ item.is_active == 1 ? 'ACTIVE' : 'INACTIVE' }}
                </v-chip>
              </template>

              <template v-slot:[`item.name`]="{ item }">
                <div class="company-info">
                  <v-avatar size="32" class="company-avatar mr-3">
                    <v-img
                      :src="item.logo_path ? `${mediaUrl + item.logo_path}` : require('../../assets/sapp-only-logo.png')"
                      alt="Company Logo"
                    />
                  </v-avatar>
                  <div class="company-details">
                    <div class="company-name">{{ item.name || '' }}</div>
                  </div>
                </div>
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <v-btn
                  color="primary"
                  small
                  @click="changeCompany(item.id)"
                  class="switch-btn"
                  :loading="isSwitching"
                >
                  <v-icon left small>mdi-account-switch</v-icon>
                  Switch
                </v-btn>
              </template>
            </v-data-table>
          </div>
        </v-card-text>

        <!-- Footer Actions -->
        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="show_company_dialog = false"
            class="cancel-btn"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script type="text/javascript">
import { mapActions } from "vuex";
import axios from "@/axios";
import store from "@/store";

export default {
  data() {
    return {
      form: {
        search_keyword: "",
      },
      hide_bell: false,
      drawer: true,
      is_acting_admin: false,
      companyName: "",
      selectedCompanyId: "",
      company_data: {},
      companies: [],
      is_company_user: false,
      isLoading: false,
      isSwitching: false,
      show_company_dialog: false,
      availableAgents: [],
      companyHeaders: [
        { text: 'Sr. No.', value: 'sr_no', sortable: false, width: '80px' },
        { text: 'Status', value: 'is_active', sortable: true, width: '120px' },
        { text: 'Company', value: 'name', sortable: true, width: '250px' },
        { text: 'Email', value: 'email', sortable: false, width: '200px' },
        { text: 'Admin', value: 'admin_name', sortable: false, width: '150px' },
        { text: 'Actions', value: 'actions', sortable: false, width: '120px', align: 'center' },
      ],
    };
  },
  computed: {
    user() {
      return this.$store.getters["auth/user"] || {};
    },
    company() {
      return this.$store.getters["auth/company"] || {};
    },
    roleName() {
      return this.$store.getters["auth/roleName"] || '';
    },
    mediaUrl() {
      return this.$store.getters["settings/mediaUrl"] || '';
    },
    encryptIt() {
      // Use a function from utilities or create a simple one
      return (id) => btoa(id); // Simple base64 encoding as fallback
    }
  },
  watch: {
    // Watch for company changes and reload agents
    company: {
      handler(newCompany, oldCompany) {
        try {
          // Only reload if company actually changed and has an ID
          if (newCompany && newCompany.id && 
              (!oldCompany || oldCompany.id !== newCompany.id) &&
              (this.roleName === 'ADMIN' || (this.roleName === 'SUPER ADMIN' && newCompany.id != null))) {
            this.loadAvailableAgents();
          }
        } catch (error) {
          console.error('Error in company watcher:', error);
        }
      },
      deep: true
    }
  },
  mounted() {
    try {
      let company = store.getters["auth/company"] || {};
      if (Object.keys(company).length > 0) {
        this.selectedCompanyId = company.id;
        if (this.roleName == "SUPER ADMIN") {
          this.is_acting_admin = true;
          console.log(this.is_acting_admin);
        }
        this.is_company_user = company ? true : false;
      }
      
      // Load available agents for Admin users
      if (this.roleName === 'ADMIN' || (this.roleName === 'SUPER ADMIN' && this.company && this.company.id != null)) {
        this.loadAvailableAgents();
      }
    } catch (error) {
      console.error('Error in mounted lifecycle:', error);
    }
  },
  methods: {
    ...mapActions({
      logOut: "auth/logOut",
    }),
    logout() {
      this.logOut();
      location.replace("/");
    },
    isActingAdmin() {
      let response = false;
      if (this.roleName == "SUPER ADMIN" && this.company.id != null) {
        response = true;
      }
      return response;
    },
    // Scroll back to the 1st validation error Message
    scrollToSection(selector) {
      // Example: smooth scroll to the specific section
      const element = document.querySelector(selector);
      const elementRect = element.getBoundingClientRect();
      const absoluteElementTop = elementRect.top + window.pageYOffset;
      const middle = absoluteElementTop - window.innerHeight / 2;
      window.scrollTo({
        top: middle,
        behavior: "smooth",
      });
      console.log(selector);
    },
    async getCompanies() {
      this.isLoading = true;
      this.form.search_keyword = this.form.search_keyword
        ? this.form.search_keyword
        : "";
      this.form.is_active = this.form.is_active ? this.form.is_active : "";
      let companies = [];
      let query =
        "search_keyword=" +
        this.form.search_keyword +
        "&is_active=" +
        this.form.is_active;
      companies = await axios.get(`companies?${query}`);
      this.companies = companies.data.data;
      this.count = companies.data.count;
      this.isLoading = false;
    },
    changeCompany(companyId) {
      this.selectedCompanyId = companyId;
      let company = this.companies.find((s) => s.id == this.selectedCompanyId);
      if (company) {
        console.log(company);
        store.dispatch("auth/setCompany", company);
        this.is_company_user = true;
      } else {
        store.dispatch("auth/setCompany", "");
        this.is_company_user = false;
      }
      window.location.href = "/auth/login";
      this.isSwitching = false;
      this.show_company_dialog = false;
      // this.$router.push("/auth/login");
    },
    switch_to(companyId = "") {
      this.isSwitching = true;
      console.log(this.is_acting_admin);
      if (this.is_acting_admin == true) {
        console.log("CompanyID " + companyId);
        console.log("Already SuperAdmin Switching to Admin");
        this.show_company_dialog = true;
      } else {
        console.log("Already Acting Admin Switching to SuperAdmin");
        this.selectedCompanyId = null;
        this.changeCompany();
      }
    },
    async loadAvailableAgents() {
      try {
        // Only load if we have a company and role
        if (!this.company || !this.company.id || !this.roleName) {
          this.availableAgents = [];
          return;
        }

        // Fetch agents with admin filter to only get agents from active subscriptions
        const response = await axios.get('/ai-agents', {
          params: {
            admin_filter: 'true',
            _t: Date.now() // Cache busting parameter
          }
        });
        
        this.availableAgents = response.data.data || [];
      } catch (error) {
        console.error('Error loading available agents:', error);
        this.availableAgents = [];
      }
    },
    getAgentIcon(agentName) {
      // Return appropriate icon based on agent name
      if (agentName === 'ContentMod AI') {
        return 'mdi-shield-check';
      } else if (agentName === 'LeadRanker AI') {
        return 'mdi-trending-up';
      } else if (agentName === 'PitchCrafter AI') {
        return 'mdi-sparkles';
      } else if (agentName === 'FollowUpBot AI') {
        return 'mdi-email-fast';
      } else if (agentName === 'CRMGenie') {
        return 'mdi-database-cog';
      }
      // Default icon for other agents
      return 'mdi-robot-excited';
    },
    getAgentSlug(agentName) {
      // Convert agent name to URL-friendly slug
      // Handle specific agent name patterns
      if (agentName === 'ContentMod AI') return 'contentmod-ai';
      if (agentName === 'LeadRanker AI') return 'leadranker-ai';
      if (agentName === 'PitchCrafter AI') return 'pitchcrafter-ai';
      if (agentName === 'FollowUpBot AI') return 'followupbot-ai';
      if (agentName === 'CRMGenie') return 'crmgenie-ai';
      
      // Fallback for other agents
      return agentName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    },
  },
};
</script>
<style scoped>
/* Modern App Bar */
.modern-app-bar {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.3) !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

/* Navigation Container */
.nav-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 0 16px;
  position: relative;
}

.nav-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
  position: absolute;
  left: 16px;
}

.nav-right {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
  position: absolute;
  right: 16px;
}

/* Navigation Elements */
.nav-icon {
  color: white !important;
  margin-right: 0 !important;
}

.logo-link {
  text-decoration: none;
  display: flex;
  align-items: center;
}

.app-logo {
  display: flex;
  align-items: center;
  gap: 12px;
}

.app-title {
  color: white;
  font-weight: 600;
  font-size: 18px;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

/* User Info Section */
.user-info {
  display: flex;
  align-items: center;
  max-width: 400px;
  min-width: 200px;
  height: 64px;
}

.user-details {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
  overflow: hidden;
  flex: 1;
  min-width: 0;
  height: 100%;
  white-space: nowrap;
}

.user-name {
  color: white !important;
  font-weight: 600;
  font-size: 15px;
  white-space: nowrap;
  flex-shrink: 0;
}

.separator {
  color: rgba(255, 255, 255, 0.6);
  font-weight: bold;
  font-size: 14px;
  flex-shrink: 0;
}

.company-chip {
  border-color: rgba(255, 255, 255, 0.6) !important;
  color: rgba(255, 255, 255, 0.95) !important;
  background: rgba(255, 255, 255, 0.15) !important;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-size: 11px !important;
  height: 22px !important;
  min-width: 80px;
  flex-shrink: 0;
}

.company-chip >>> .v-chip__content {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Profile Menu */
.profile-menu {
  flex-shrink: 0;
  height: 64px;
  display: flex;
  align-items: center;
}

.profile-button {
  color: white !important;
  background: rgba(255, 255, 255, 0.1) !important;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
}

.profile-button:hover {
  background: rgba(255, 255, 255, 0.2) !important;
  transform: scale(1.05);
}

/* Super Admin Switch */
.super-admin-switch {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 64px;
  margin-right: 8px;
}

.super-admin-switch >>> .v-input--switch {
  margin: 0;
  padding: 0;
}

.super-admin-switch >>> .v-input--switch .v-input__slot {
  margin: 0;
  min-height: auto;
}

.super-admin-switch >>> .v-input--switch .v-label {
  color: white !important;
  font-weight: 500;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-left: 8px;
}

.super-admin-switch >>> .v-input--switch__thumb {
  color: white !important;
}

.super-admin-switch >>> .v-input--switch__track {
  color: rgba(255, 255, 255, 0.4) !important;
}

.super-admin-switch >>> .v-input--switch.v-input--is-label-active .v-input--switch__track {
  color: rgba(255, 255, 255, 0.6) !important;
}

/* Modern Navigation Drawer */
.modern-drawer {
  background: linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%) !important;
  border-right: 1px solid rgba(102, 126, 234, 0.1) !important;
}

.drawer-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 24px 20px;
  margin-bottom: 16px;
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 16px;
}

.logo-text h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: white;
}

.logo-text p {
  margin: 0;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 400;
}

.user-profile-section {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: #ffffff;
  margin: 16px 12px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.user-profile-section .user-details {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.user-avatar {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
}

.user-details .user-name {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #000000 !important;
}

.user-details .user-role {
  margin: 0;
  font-size: 12px;
  color: #000000 !important;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Navigation List */
.navigation-list {
  padding: 0 12px;
}

.navigation-list .v-list-item {
  border-radius: 12px !important;
  margin-bottom: 4px;
  transition: all 0.3s ease;
}

.navigation-list .v-list-item:hover {
  background: rgba(102, 126, 234, 0.08) !important;
  transform: translateX(4px);
}

.navigation-list .v-list-item--active {
  background: rgba(102, 126, 234, 0.15) !important;
  color: #667eea !important;
}

.navigation-list .v-list-item--active .v-icon {
  color: #667eea !important;
}

.navigation-list .v-list-item--active .v-list-item-title {
  color: #667eea !important;
  font-weight: 600;
}

.navigation-list .v-list-item-icon {
  margin-right: 16px !important;
}

.navigation-list .v-list-item-icon .v-icon {
  color: #667eea !important;
  transition: all 0.3s ease;
}

.navigation-list .v-list-item-title {
  font-weight: 500;
  color: #2c3e50;
  font-size: 15px;
}

/* List Groups */
.navigation-list .v-list-group .v-list-group__header {
  border-radius: 12px !important;
  margin-bottom: 4px;
}

.navigation-list .v-list-group .v-list-group__header:hover {
  background: rgba(102, 126, 234, 0.08) !important;
}

.navigation-list .v-list-group--active .v-list-group__header {
  background: rgba(102, 126, 234, 0.1) !important;
}

.navigation-list .v-list-group__items {
  background: rgba(102, 126, 234, 0.02) !important;
  margin: 0 8px;
  border-radius: 0 0 12px 12px;
  padding: 4px 0;
}

.navigation-list .v-list-group__items .v-list-item {
  padding-left: 48px !important;
  padding-right: 16px !important;
  font-size: 14px;
  margin: 2px 8px;
  border-radius: 8px !important;
  min-height: 40px !important;
}

.navigation-list .v-list-group__items .v-list-item:hover {
  background: rgba(102, 126, 234, 0.08) !important;
  transform: translateX(4px);
}

.navigation-list .v-list-group__items .v-list-item--active {
  background: rgba(102, 126, 234, 0.15) !important;
  color: #667eea !important;
  font-weight: 600;
}

.navigation-list .v-list-group__items .v-list-item .v-list-item-title {
  font-size: 13px;
  color: #546e7a;
}

.navigation-list .v-list-group__items .v-list-item--active .v-list-item-title {
  color: #667eea !important;
  font-weight: 600;
}

.navigation-list .v-list-group__items .v-list-item .v-list-item-icon {
  margin-right: 12px !important;
  min-width: 20px !important;
}

.navigation-list .v-list-group__items .v-list-item .v-list-item-icon .v-icon {
  font-size: 18px !important;
  color: #78909c !important;
}

.navigation-list .v-list-group__items .v-list-item--active .v-list-item-icon .v-icon {
  color: #667eea !important;
}

/* Sub-group styling for nested lists like Trash > Settings */
.navigation-list .v-list-group--sub-group .v-list-group__items {
  background: rgba(102, 126, 234, 0.03) !important;
  margin-left: 16px;
}

.navigation-list .v-list-group--sub-group .v-list-group__items .v-list-item {
  padding-left: 32px !important;
}

/* Logout Button Styling */
.logout-item {
  border-radius: 12px !important;
  margin: 8px 12px !important;
  background: rgba(239, 68, 68, 0.1) !important;
  border: 1px solid rgba(239, 68, 68, 0.2);
  transition: all 0.3s ease;
}

.logout-item:hover {
  background: rgba(239, 68, 68, 0.15) !important;
  transform: translateX(4px);
}

.logout-text {
  color: #ef4444 !important;
  font-weight: 600 !important;
}

/* Navigation Wrapper */
.navigation-wrapper {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 200px);
  justify-content: space-between;
}

/* Logout Section */
.logout-section {
  margin-top: auto;
}

/* User Menu */
.v-menu__content .v-list-item {
  transition: all 0.3s ease;
}

.v-menu__content .v-list-item:hover {
  background: rgba(102, 126, 234, 0.08) !important;
}

/* Main Content Spacing */
.v-main__wrap {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

/* Responsive */
@media (max-width: 1200px) {
  .user-info {
    max-width: 200px;
  }
  
  .user-name {
    font-size: 14px;
    max-width: 180px;
  }
  
  .company-chip {
    max-width: 140px;
    font-size: 9px !important;
    height: 18px !important;
  }
}

/* Responsive Navigation Design */
@media (max-width: 1400px) {
  .nav-container {
    padding: 0 12px;
  }
  
  .nav-right {
    gap: 12px;
  }
  
  .user-info {
    max-width: 200px;
  }
  
  .user-name {
    max-width: 140px;
  }
}

@media (max-width: 1200px) {
  .nav-container {
    padding: 0 8px;
  }
  
  .nav-right {
    gap: 8px;
  }
  
  .user-info {
    max-width: 160px;
  }
  
  .user-name {
    font-size: 13px;
    max-width: 140px;
  }
  
  .company-chip {
    max-width: 120px;
    font-size: 8px !important;
    height: 16px !important;
  }
  
  .super-admin-switch >>> .v-label {
    font-size: 10px;
  }
}

@media (max-width: 960px) {
  .nav-left {
    gap: 8px;
  }
  
  .nav-right {
    gap: 6px;
  }
  
  .user-info {
    max-width: 120px;
    min-width: 80px;
  }
  
  .user-name {
    font-size: 11px;
    max-width: 80px;
  }
  
  .company-chip {
    max-width: 70px;
    font-size: 7px !important;
    height: 14px !important;
  }
  
  .app-title {
    font-size: 16px;
  }
  
  .super-admin-switch {
    margin-right: 4px;
  }
  
  .super-admin-switch >>> .v-label {
    font-size: 9px;
  }
}

@media (max-width: 768px) {
  .nav-container {
    padding: 0 4px;
  }
  
  .nav-left {
    gap: 4px;
  }
  
  .nav-right {
    gap: 4px;
  }
  
  .app-title {
    display: none;
  }
  
  .user-info {
    max-width: 100px;
    min-width: 60px;
  }
  
  .user-name {
    font-size: 10px;
    max-width: 60px;
  }
  
  .company-chip {
    display: none;
  }
  
  .super-admin-switch >>> .v-label {
    display: none;
  }
}

@media (max-width: 600px) {
  .user-info {
    display: none;
  }
  
  .super-admin-switch {
    margin-right: 0;
  }
  
  .nav-right {
    gap: 8px;
  }
  
  .modern-drawer {
    transform: translateX(-280px);
    transition: transform 0.3s ease;
  }
  
  .modern-drawer.v-navigation-drawer--open {
    transform: translateX(0);
  }
}

@media (max-width: 480px) {
  .super-admin-switch {
    display: none !important;
  }
  
  .nav-container {
    padding: 0 2px;
  }
  
  .nav-left {
    gap: 2px;
  }
  
  .profile-button {
    width: 36px !important;
    height: 36px !important;
    min-width: 36px !important;
  }
  
  .profile-button >>> .v-avatar {
    width: 32px !important;
    height: 32px !important;
  }
}

/* Loading and transition states */
.nav-container {
  transition: all 0.3s ease;
}

.user-info,
.super-admin-switch,
.profile-button {
  transition: all 0.3s ease;
}

/* Focus states for accessibility */
.profile-button:focus {
  outline: 2px solid rgba(255, 255, 255, 0.8);
  outline-offset: 2px;
}

.nav-icon:focus {
  outline: 2px solid rgba(255, 255, 255, 0.8);
  outline-offset: 2px;
  border-radius: 50%;
}

/* Switch to Admin Dialog */
.switch-admin-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.dialog-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  padding: 24px !important;
  border-radius: 0 !important;
}

.dialog-header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.dialog-logo-section {
  display: flex;
  align-items: center;
  gap: 16px;
}

.dialog-logo-wrapper {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dialog-title-section {
  flex: 1;
}

.dialog-title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: white;
  line-height: 1.2;
}

.dialog-subtitle {
  margin: 4px 0 0 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 400;
}

.close-btn {
  background: rgba(255, 255, 255, 0.1) !important;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
}

.dialog-content {
  padding: 32px !important;
  background: #f8f9fa;
}

.search-section {
  max-width: 400px;
}

.modern-search >>> .v-input__control .v-input__slot {
  border-radius: 12px !important;
  border-color: rgba(102, 126, 234, 0.2) !important;
  background: white !important;
}

.modern-search >>> .v-input--is-focused .v-input__control .v-input__slot {
  border-color: #667eea !important;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1) !important;
}

.loading-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 16px;
}

.loading-text {
  color: #64748b;
  font-size: 16px;
  margin: 0;
}

.companies-table-section {
  background: white;
  border-radius: 12px;
  border: 1px solid rgba(102, 126, 234, 0.1);
  overflow: hidden;
}

.modern-companies-table {
  background: transparent !important;
}

.modern-companies-table >>> .v-data-table__wrapper {
  background: white;
}

.modern-companies-table >>> thead tr th {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  border: none !important;
  border-right: 1px solid rgba(255, 255, 255, 0.2) !important;
  padding: 16px 12px !important;
}

.modern-companies-table >>> thead tr th:last-child {
  border-right: none !important;
}

.modern-companies-table >>> tbody tr {
  transition: all 0.3s ease;
}

.modern-companies-table >>> tbody tr:hover {
  background: rgba(102, 126, 234, 0.05) !important;
}

.modern-companies-table >>> tbody tr td {
  padding: 16px 12px !important;
  border-bottom: 1px solid rgba(102, 126, 234, 0.1) !important;
  border-right: 1px solid rgba(102, 126, 234, 0.1) !important;
}

.modern-companies-table >>> tbody tr td:last-child {
  border-right: none !important;
}

.company-serial {
  font-weight: 600;
  font-size: 14px;
  color: #64748b;
  background: rgba(102, 126, 234, 0.08);
  padding: 4px 8px;
  border-radius: 6px;
  display: inline-block;
  min-width: 24px;
  text-align: center;
}

.status-chip {
  border-radius: 8px !important;
  font-weight: 500 !important;
}

.company-info {
  display: flex;
  align-items: center;
}

.company-avatar {
  border: 2px solid rgba(102, 126, 234, 0.1);
}

.company-name {
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
}

.switch-btn {
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
}

.dialog-actions {
  background: #f8f9fa;
  border-top: 1px solid rgba(102, 126, 234, 0.1);
  padding: 16px 24px !important;
}

.cancel-btn {
  color: #64748b !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
}

.cancel-btn:hover {
  background: rgba(100, 116, 139, 0.1) !important;
}

/* Legacy styles */
.nav-text {
  text-decoration: none;
  color: black;
  font-weight: bold;
  font-size: 20px;
}

.v-application--is-ltr
  .v-list--dense.v-list--nav
  .v-list-group--no-action
  > .v-list-group__items
  > .v-list-item {
  padding: 0 8px 0 20px;
}
</style>
